import config from '@/utils/config';
import { EmbeddedMap } from '@pathccm/path-components-provider-search';
import { Address, ProviderPracticeAddress } from '@/utils/types';
import useData from '@/state/use-data';

export interface MapProps {
  providerAddress: ProviderPracticeAddress;
  visible?: boolean;
}

type FormatAddressProps =
  | {
      type: 'patient';
      address: Address;
    }
  | {
      type: 'provider';
      address: ProviderPracticeAddress;
    };

// Normalize the address format for both patient and provider and return a formatted string
const formatAddress = ({ type, address }: FormatAddressProps) => {
  const { street, apt_suite, city, state, zip }: Address =
    type === 'patient'
      ? address
      : {
          street: address.street1,
          apt_suite: address.street2,
          city: address.city,
          state: address.state,
          zip: address.zip,
        };
  const streetLine = `${street}${apt_suite ? ` ${apt_suite}` : ''}`;
  return `${streetLine}, ${city}, ${state} ${zip}`;
};

export const ProviderMap = ({ providerAddress, visible = true }: MapProps) => {
  const {
    data: { profile },
  } = useData(['profile']);
  const patientAddress =
    profile.loadingState === 'done' ? profile.address : undefined;

  const apiKey = config.VITE_GOOGLE_MAPS_EMBED_API_KEY;
  if (!apiKey) {
    console.warn(
      'Google Maps Embed API key is not set. Map will not be displayed.',
    );
    return null;
  }

  // Even if the map is not visible we still render it to avoid flickering
  const className = visible ? 'md:ml-8 mt-4' : 'invisible h-0';
  return (
    <div className={className} aria-hidden={!visible}>
      <EmbeddedMap
        apiKey={apiKey}
        location={formatAddress({ type: 'provider', address: providerAddress })}
        directionsFrom={
          patientAddress &&
          formatAddress({ type: 'patient', address: patientAddress })
        }
      />
    </div>
  );
};
